import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import {
  Play, Pause, X, Clock, DoorOpen, SkipForward,
  ChevronUp, Trash2, RefreshCw,
} from 'lucide-react'
import api, { pesanGalat } from '../lib/api'

// ═══════════════════════════════════════════════════════════════════════════
//  Layar kasir — status semua room, timer, antrean.
//
//  ── SISA WAKTU DIHITUNG DARI SERVER, DIMULUSKAN DI LAYAR ──────────────────
//
//  Server mengirim `sisa_detik` yang dihitung dari `berakhir_at`. Layar
//  menurunkannya per detik supaya angkanya bergerak halus, TAPI setiap
//  penyegaran (10 detik) menimpanya dengan angka server.
//
//  Kalau layar menghitung sendiri tanpa ditimpa, tab yang dibiarkan terbuka
//  semalaman akan meleset jauh — timer JavaScript melambat saat tab tidak
//  aktif, dan meleset ke arah yang menguntungkan tamu.
// ═══════════════════════════════════════════════════════════════════════════

type Room = {
  room_id: string; outlet_id: string; outlet_nama: string
  room_kode: string | null; room_nama: string; room_tipe: string | null
  sesi_id: string | null
  status: 'kosong' | 'siap' | 'berjalan' | 'jeda' | 'habis' | 'selesai'
  tamu: string | null
  paket_menit: number | null; perpanjang_menit: number | null
  sisa_detik: number | null
  antre: number
  lagu_sekarang: string | null
  // 5 Okt 2026: booking ElPOS yang menempel di sesi + perangkat terpasang.
  perangkat?: number
  booking_id?: string | null
  booking_nomor?: string | null
  booking_status?: 'booked' | 'berjalan' | 'selesai' | 'void' | null
  booking_mulai?: string | null
  booking_selesai?: string | null
  booking_tamu?: string | null
}

// 5 Okt 2026: booking ElPOS hari ini yang tidak bisa jadi sesi (handlers/sesi.go PeringatanRoom).
type Peringatan = {
  jenis: 'room_nonaktif' | 'room_outlet_lain' | 'menunggu_room' | 'tanpa_perangkat' | 'tanpa_room'
  tingkat: 'peringatan' | 'info'
  outlet_nama: string; room_nama: string | null
  booking_nomor: string | null; mulai: string | null
  pesan: string
}

const jamWIB = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : ''

type Outlet = { id: string; kode: string; nama: string; jml_room: number }

type Antre = {
  id: string; urutan: number; status: string; geser_nada: number
  diminta_oleh: string | null; judul: string; artis: string
  durasi_detik: number | null; nada_dasar: string | null
  ada_lirik_sinkron: boolean
}

const jam = (d: number | null) => {
  if (d == null) return '—'
  const n = Math.max(0, d)
  const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), s = n % 60
  return (h > 0 ? `${h}:` : '') + `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const GAYA: Record<Room['status'], string> = {
  kosong:   'bg-bg-2 border-border',
  siap:     'bg-sky-500/5 border-sky-500/30',
  berjalan: 'bg-emerald-500/5 border-emerald-500/30',
  jeda:     'bg-amber-500/5 border-amber-500/30',
  habis:    'bg-red-500/10 border-red-500/40',
  selesai:  'bg-bg-2 border-border',
}

const LABEL: Record<Room['status'], string> = {
  kosong: 'Kosong', siap: 'Siap', berjalan: 'Berjalan',
  jeda: 'Jeda', habis: 'WAKTU HABIS', selesai: 'Selesai',
}

export default function RoomPage() {
  const [outlets, setOutlets] = useState<Outlet[]>([])
  const [outlet, setOutlet] = useState('')
  const [rooms, setRooms] = useState<Room[]>([])
  const [galat, setGalat] = useState('')
  const [memuat, setMemuat] = useState(false)
  const [buka, setBuka] = useState<Room | null>(null)
  const [antreUntuk, setAntreUntuk] = useState<Room | null>(null)
  const [kodeUntuk, setKodeUntuk] = useState<Room | null>(null)
  const [tabletUntuk, setTabletUntuk] = useState<Room | null>(null)
  const [peringatan, setPeringatan] = useState<Peringatan[]>([])
  const [lihatInfo, setLihatInfo] = useState(false)
  const terakhirSegar = useRef(Date.now())

  useEffect(() => {
    api.get('/api/outlet')
      .then(r => {
        const d: Outlet[] = r.data ?? []
        setOutlets(d)
        // 28 Okt: akun yang cuma punya satu outlet langsung diarahkan ke situ.
        if (d.length === 1) setOutlet(d[0].id)
      })
      .catch(e => setGalat(pesanGalat(e, 'Gagal memuat outlet')))
  }, [])

  const muat = useCallback(async () => {
    setMemuat(true)
    try {
      const p = outlet ? `?outlet_id=${outlet}` : ''
      setRooms((await api.get(`/api/room${p}`)).data ?? [])
      setGalat('')
      // Peringatan booking: gagal dimuat tidak menghentikan layar room.
      api.get(`/api/room/peringatan${p}`)
        .then(r => setPeringatan(r.data ?? []))
        .catch(() => { /* backend lama / jaringan — diam */ })
      terakhirSegar.current = Date.now()
    } catch (e) {
      setGalat(pesanGalat(e, 'Gagal memuat status room'))
    } finally { setMemuat(false) }
  }, [outlet])

  useEffect(() => { muat() }, [muat])

  // Penyegaran 10 detik. Angka server SELALU menang atas hitungan layar.
  useEffect(() => {
    const t = setInterval(muat, 10_000)
    return () => clearInterval(t)
  }, [muat])

  // Penurunan halus per detik, hanya untuk yang sedang berjalan.
  // 'jeda' TIDAK ikut turun — itu inti dari jeda.
  useEffect(() => {
    const t = setInterval(() => {
      setRooms(rs => rs.map(r =>
        r.status === 'berjalan' && r.sisa_detik != null
          ? { ...r, sisa_detik: Math.max(0, r.sisa_detik - 1) }
          : r))
    }, 1000)
    return () => clearInterval(t)
  }, [])

  async function aksi(r: Room, a: string, menit?: number) {
    setGalat('')
    try {
      await api.post(`/api/sesi/${r.sesi_id}/aksi`, { aksi: a, menit })
      await muat()
    } catch (e) { setGalat(pesanGalat(e, `Gagal ${a}`)) }
  }

  const terpakai = rooms.filter(r => r.status !== 'kosong').length
  const perluTindak = rooms.filter(r => r.status === 'habis').length

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5 flex-wrap">
        <select value={outlet} onChange={e => setOutlet(e.target.value)}>
          <option value="">Semua outlet</option>
          {outlets.map(o => (
            <option key={o.id} value={o.id}>{o.nama} ({o.jml_room} room)</option>
          ))}
        </select>
        <span className="text-[12px] text-text-3">
          {terpakai}/{rooms.length} terpakai
        </span>
        {perluTindak > 0 && (
          <span className="text-[11.5px] px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
            {perluTindak} room waktunya habis
          </span>
        )}
        <div className="flex-1" />
        <button onClick={muat}
          className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-bg-2
                     border border-border text-text-2 hover:border-gold-2 transition-colors">
          <RefreshCw size={13} className={memuat ? 'animate-spin' : ''} /> Segarkan
        </button>
      </div>

      {galat && (
        <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
          {galat}
        </div>
      )}

      {/* 5 Okt 2026: booking ElPOS yang tidak bisa jadi sesi — ditampilkan,
          bukan diam-diam dilewati. Info (booking tanpa ruangan) dilipat. */}
      {peringatan.some(p => p.tingkat === 'peringatan') && (
        <div className="text-[12px] bg-amber-500/10 border border-amber-500/30 rounded-xl px-3.5 py-2.5 space-y-1.5">
          <div className="font-semibold text-amber-300">
            ⚠ {peringatan.filter(p => p.tingkat === 'peringatan').length} booking ElPOS perlu dicek
          </div>
          <ul className="space-y-1 text-text-2">
            {peringatan.filter(p => p.tingkat === 'peringatan').map((p, i) => (
              <li key={i} className="leading-snug">
                <span className="text-text">
                  {[p.outlet_nama, p.room_nama, p.booking_nomor, jamWIB(p.mulai)].filter(Boolean).join(' · ')}
                </span>
                {' — '}{p.pesan}
              </li>
            ))}
          </ul>
        </div>
      )}
      {peringatan.some(p => p.tingkat === 'info') && (
        <div className="text-[11.5px] text-text-3">
          <button onClick={() => setLihatInfo(v => !v)} className="underline underline-offset-2">
            {lihatInfo ? 'Sembunyikan' : 'Lihat'} info booking tanpa ruangan
          </button>
          {lihatInfo && (
            <ul className="mt-1 space-y-0.5">
              {peringatan.filter(p => p.tingkat === 'info').map((p, i) => (
                <li key={i}>{p.outlet_nama}: {p.pesan}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rooms.length === 0 && !memuat ? (
        <div className="bg-bg-1 border border-border rounded-xl p-12 text-center text-[13px] text-text-3 leading-relaxed">
          Belum ada room aktif.<br />
          <span className="text-[12px]">
            Room diambil dari master ruangan ElPOS — karaoke tidak punya daftar sendiri.
            Tambahkan di ElPOS, lalu segarkan halaman ini.
          </span>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.map(r => (
            <div key={r.room_id} className={`border rounded-xl p-4 space-y-3 transition-colors ${GAYA[r.status]}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-[14px] leading-tight">
                    {r.room_nama}
                    {r.room_kode && <span className="text-text-3 font-normal text-[11px] ml-1.5">{r.room_kode}</span>}
                  </div>
                  <div className="text-[10.5px] text-text-3 mt-0.5">
                    {r.outlet_nama}{r.room_tipe ? ` · ${r.room_tipe}` : ''}
                    {' · '}
                    <button onClick={() => setTabletUntuk(r)} className="text-gold/80 underline underline-offset-2">tablet kamar</button>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-md border shrink-0 ${
                  r.status === 'habis' ? 'bg-red-500/15 text-red-400 border-red-500/40'
                  : r.status === 'berjalan' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                  : r.status === 'jeda' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  : 'bg-white/5 text-text-3 border-border'}`}>
                  {LABEL[r.status]}
                </span>
              </div>

              {r.status === 'kosong' ? (
                <button onClick={() => setBuka(r)}
                  className="w-full flex items-center justify-center gap-1.5 text-[12.5px] py-2 rounded-lg
                             bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 transition-colors">
                  <DoorOpen size={14} /> Buka room
                </button>
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <Clock size={14} className="text-text-3 shrink-0" />
                    <span className={`text-2xl font-semibold tabular-nums ${
                      r.status === 'habis' ? 'text-red-400'
                      : (r.sisa_detik ?? 0) < 300 ? 'text-amber-400' : ''}`}>
                      {r.status === 'siap' ? '—' : jam(r.sisa_detik)}
                    </span>
                    <span className="text-[11px] text-text-3">
                      {r.paket_menit}m
                      {(r.perpanjang_menit ?? 0) > 0 && ` +${r.perpanjang_menit}m`}
                    </span>
                  </div>

                  {r.tamu && <div className="text-[12px] text-text-2">{r.tamu}</div>}

                  {/* 5 Okt 2026: sesi dari booking ElPOS. */}
                  {r.booking_id && (
                    <div className="text-[11px] text-sky-300/90 leading-snug">
                      📅 Booking {r.booking_nomor ?? ''} · {jamWIB(r.booking_mulai)}–{jamWIB(r.booking_selesai)}
                      {r.booking_status === 'booked' && r.status === 'siap' && (
                        <div className="text-text-3">Berjalan otomatis saat booking dimulai di ElPOS, atau tekan Mulai.</div>
                      )}
                    </div>
                  )}
                  {(r.perangkat ?? 0) === 0 && (
                    <div className="text-[10.5px] text-amber-300/90">
                      TV/tablet belum terpasang — sesi tercatat, lagu tidak bisa diputar.
                    </div>
                  )}

                  <div className="text-[11.5px] text-text-3 leading-relaxed min-h-[2.2em]">
                    {r.lagu_sekarang
                      ? <>♪ {r.lagu_sekarang}</>
                      : <span className="opacity-60">tidak ada lagu diputar</span>}
                    {r.antre > 0 && <div className="text-[10.5px]">{r.antre} lagu menunggu</div>}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {r.status === 'siap' && (
                      <Tombol onClick={() => aksi(r, 'mulai')} warna="hijau">
                        <Play size={12} /> Mulai
                      </Tombol>
                    )}
                    {r.status === 'berjalan' && (
                      <Tombol onClick={() => aksi(r, 'jeda')}><Pause size={12} /> Jeda</Tombol>
                    )}
                    {r.status === 'jeda' && (
                      <Tombol onClick={() => aksi(r, 'lanjut')} warna="hijau">
                        <Play size={12} /> Lanjut
                      </Tombol>
                    )}
                    {r.status !== 'siap' && (
                      <>
                        <Tombol onClick={() => aksi(r, 'perpanjang', 30)}>+30m</Tombol>
                        <Tombol onClick={() => aksi(r, 'perpanjang', 60)}>+60m</Tombol>
                      </>
                    )}
                    <Tombol onClick={() => setAntreUntuk(r)}>
                      <ListIcon /> Antrean
                    </Tombol>
                    <Tombol onClick={() => setKodeUntuk(r)}>Kode tamu</Tombol>
                    <Tombol warna="merah" onClick={() => {
                      if (confirm(`Tutup sesi ${r.room_nama}?`)) aksi(r, 'tutup')
                    }}>Tutup</Tombol>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {buka && <FormBuka room={buka} tutup={() => setBuka(null)}
                 selesai={() => { setBuka(null); muat() }} />}
      {antreUntuk && <PanelAntrean room={antreUntuk}
                       tutup={() => { setAntreUntuk(null); muat() }} />}
      {kodeUntuk && <PanelKode room={kodeUntuk} tutup={() => setKodeUntuk(null)} />}
      {tabletUntuk && <PanelTablet room={tabletUntuk} tutup={() => setTabletUntuk(null)} />}
    </div>
  )
}

function ListIcon() {
  return <span className="text-[11px] leading-none">☰</span>
}

function Tombol({ children, onClick, warna }: {
  children: ReactNode; onClick: () => void; warna?: 'hijau' | 'merah'
}) {
  const g = warna === 'hijau'
    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
    : warna === 'merah'
    ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
    : 'bg-bg-3 border-border text-text-2 hover:border-gold-2'
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1 text-[11.5px] px-2.5 py-1.5 rounded-md border transition-colors ${g}`}>
      {children}
    </button>
  )
}

function FormBuka({ room, tutup, selesai }: { room: Room; tutup: () => void; selesai: () => void }) {
  const [paket, setPaket] = useState(60)
  const [tamu, setTamu] = useState('')
  const [galat, setGalat] = useState('')
  const [simpan, setSimpan] = useState(false)

  async function kirim() {
    setSimpan(true); setGalat('')
    try {
      await api.post('/api/sesi', { room_id: room.room_id, paket_menit: paket, tamu })
      selesai()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal membuka room')) }
    finally { setSimpan(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-1 border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Buka {room.room_nama}</h2>
            <p className="text-[11.5px] text-text-3 mt-0.5">{room.outlet_nama}</p>
          </div>
          <button onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>

        <div className="space-y-1.5">
          <label className="text-[12px] text-text-2">Paket waktu</label>
          <div className="flex gap-1.5 flex-wrap">
            {[60, 90, 120, 180].map(m => (
              <button key={m} onClick={() => setPaket(m)}
                className={`text-[12px] px-3 py-1.5 rounded-lg border transition-colors ${
                  paket === m ? 'bg-gold/10 border-gold/30 text-gold'
                              : 'bg-bg-3 border-border text-text-2'}`}>
                {m >= 60 ? `${m / 60} jam` : `${m}m`}
              </button>
            ))}
          </div>
        </div>

        <input value={tamu} onChange={e => setTamu(e.target.value)}
          placeholder="Nama tamu (opsional)" className="w-full" />

        {/* Kalimat ini menjelaskan kenapa timernya belum jalan setelah room
            dibuka — tanpa itu, staf akan menekan Buka lalu bingung mencari
            angka yang tidak bergerak. */}
        <p className="text-[11px] text-text-3 leading-relaxed">
          Room dibuka dalam status <b className="text-text-2">Siap</b>. Waktu baru berjalan
          saat Anda menekan <b className="text-text-2">Mulai</b> — jeda antara membuka pintu
          dan tamu benar-benar mulai tidak dihitung.
        </p>

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
            {galat}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={tutup}
            className="text-[13px] px-4 py-2 rounded-lg bg-bg-3 border border-border text-text-2">Batal</button>
          <button onClick={kirim} disabled={simpan}
            className="text-[13px] px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold
                       hover:bg-gold/25 transition-colors disabled:opacity-50">
            {simpan ? 'Membuka…' : 'Buka room'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PanelAntrean({ room, tutup }: { room: Room; tutup: () => void }) {
  const [rows, setRows] = useState<Antre[]>([])
  const [cari, setCari] = useState('')
  const [hasil, setHasil] = useState<{ id: string; judul: string; artis: string }[]>([])
  const [galat, setGalat] = useState('')

  const muat = useCallback(async () => {
    try { setRows((await api.get(`/api/sesi/${room.sesi_id}/antrean`)).data ?? []) }
    catch (e) { setGalat(pesanGalat(e, 'Gagal memuat antrean')) }
  }, [room.sesi_id])

  useEffect(() => { muat() }, [muat])

  useEffect(() => {
    if (!cari.trim()) { setHasil([]); return }
    const t = setTimeout(async () => {
      try { setHasil((await api.get(`/api/lagu/siap?q=${encodeURIComponent(cari)}`)).data ?? []) }
      catch { /* pencarian gagal tidak boleh menutup panel */ }
    }, 250)
    return () => clearTimeout(t)
  }, [cari])

  async function tambah(songID: string) {
    setGalat('')
    try {
      await api.post(`/api/sesi/${room.sesi_id}/antrean`, { song_id: songID })
      setCari(''); setHasil([]); await muat()
    } catch (e) { setGalat(pesanGalat(e, 'Gagal menambah lagu')) }
  }

  async function aksi(a: Antre, aksi: string) {
    setGalat('')
    try { await api.post(`/api/antrean/${a.id}/aksi`, { aksi }); await muat() }
    catch (e) { setGalat(pesanGalat(e, 'Gagal')) }
  }

  const menunggu = rows.filter(r => r.status === 'menunggu')

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-lg bg-bg-1 border border-border rounded-2xl p-6 space-y-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Antrean — {room.room_nama}</h2>
            <p className="text-[11.5px] text-text-3 mt-0.5">{menunggu.length} lagu menunggu</p>
          </div>
          <button onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>

        <div className="relative">
          <input value={cari} onChange={e => setCari(e.target.value)}
            placeholder="Cari lagu untuk ditambahkan" className="w-full" />
          {hasil.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-bg-2 border border-border
                            rounded-lg max-h-52 overflow-y-auto">
              {hasil.map(h => (
                <button key={h.id} onClick={() => tambah(h.id)}
                  className="w-full text-left px-3 py-2 hover:bg-bg-3 text-[12.5px] border-b border-border/50 last:border-0">
                  {h.judul} <span className="text-text-3">— {h.artis}</span>
                </button>
              ))}
            </div>
          )}
          {cari.trim() && hasil.length === 0 && (
            <p className="text-[11px] text-text-3 mt-1.5">
              Tidak ada yang cocok. Hanya lagu berstatus <b>siap</b> yang bisa diantrekan.
            </p>
          )}
        </div>

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
            {galat}
          </div>
        )}

        <div className="overflow-y-auto flex-1 -mx-1 px-1">
          {rows.length === 0 ? (
            <p className="text-[12.5px] text-text-3 py-8 text-center">Antrean masih kosong.</p>
          ) : rows.map(a => (
            <div key={a.id}
              className={`flex items-center gap-2 py-2 border-b border-border/50 ${
                a.status === 'diputar' ? 'bg-emerald-500/5 -mx-2 px-2 rounded-lg' :
                a.status !== 'menunggu' ? 'opacity-40' : ''}`}>
              <span className="text-[11px] text-text-3 w-5 shrink-0">{a.urutan}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] truncate">{a.judul}</div>
                <div className="text-[10.5px] text-text-3 truncate">
                  {a.artis}
                  {a.geser_nada !== 0 && ` · nada ${a.geser_nada > 0 ? '+' : ''}${a.geser_nada}`}
                  {a.diminta_oleh && ` · ${a.diminta_oleh}`}
                  {!a.ada_lirik_sinkron && ' · tanpa lirik sinkron'}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {a.status === 'menunggu' && (
                  <>
                    <Mini onClick={() => aksi(a, 'putar')} title="Putar sekarang"><Play size={11} /></Mini>
                    <Mini onClick={() => aksi(a, 'naik')} title="Naikkan"><ChevronUp size={11} /></Mini>
                    <Mini onClick={() => aksi(a, 'hapus')} title="Hapus"><Trash2 size={11} /></Mini>
                  </>
                )}
                {a.status === 'diputar' && (
                  <>
                    <Mini onClick={() => aksi(a, 'selesai')} title="Tandai selesai">✓</Mini>
                    <Mini onClick={() => aksi(a, 'lewati')} title="Lewati"><SkipForward size={11} /></Mini>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Mini({ children, onClick, title }: {
  children: ReactNode; onClick: () => void; title: string
}) {
  return (
    <button onClick={onClick} title={title}
      className="w-6 h-6 grid place-items-center rounded-md bg-bg-3 border border-border
                 text-text-2 hover:border-gold-2 transition-colors text-[11px]">
      {children}
    </button>
  )
}

// ═══════════════ KODE TAMU ═══════════════════════════════════════════════
//
// Kapten membuka panel ini, membacakan/menempelkan kodenya, dan tamu memakai
// remote di HP-nya sendiri.
//
// ── DUA ALAMAT, DAN KEDUANYA DITAMPILKAN ──────────────────────────────────
//
//   /r/KODE  untuk HP tamu
//   /p/KODE  untuk perangkat yang tersambung ke TV room
//
// Alamat pemutar sengaja ikut tampil di sini walau bukan untuk tamu. Kalau
// tidak, satu-satunya yang tahu cara membuka layar TV adalah orang yang
// memasangnya — dan malam ketika ia libur, room itu tidak punya pemutar.
function PanelKode({ room, tutup }: { room: Room; tutup: () => void }) {
  const [kode, setKode] = useState<string | null>(null)
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState(true)

  useEffect(() => {
    if (!room.sesi_id) { setGalat('Room ini belum punya sesi yang dibuka.'); setSibuk(false); return }
    api.post(`/api/sesi/${room.sesi_id}/kode`)
      .then(r => setKode(r.data.kode))
      .catch(e => setGalat(pesanGalat(e)))
      .finally(() => setSibuk(false))
  }, [room.sesi_id])

  const asal = location.origin
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center px-5"
         onClick={tutup}>
      <div className="bg-bg-1 border border-border rounded-2xl w-full max-w-md p-6 text-center"
           onClick={e => e.stopPropagation()}>
        <div className="text-[12px] text-text-3 uppercase tracking-wide">
          {room.outlet_nama} · {room.room_nama}
        </div>

        {sibuk && <div className="py-10 text-[13px] text-text-3">Menyiapkan kode…</div>}
        {galat && <div className="py-8 text-[13px] text-red-400">{galat}</div>}

        {kode && (
          <>
            <div className="mt-4 text-5xl font-bold tracking-[0.18em] text-gold">{kode}</div>
            <p className="mt-3 text-[12.5px] text-text-3 leading-relaxed">
              Tamu buka <span className="text-text-2">{asal.replace(/^https?:\/\//, '')}/r</span> di
              HP, lalu ketik kode ini.
            </p>

            <div className="mt-5 pt-4 border-t border-border text-[12px] text-text-3">
              <div>Layar TV room:</div>
              <div className="mt-1 text-text-2 break-all">{asal}/p/{kode}</div>
            </div>

            {/* Kode mati saat sesi ditutup. Ditulis di sini karena kapten yang
                menutup sesi lalu membukanya lagi akan menganggap kode lamanya
                masih berlaku — dan tamu yang memakai kode lama akan bilang
                "aplikasinya error", bukan "kodenya berganti". */}
            <p className="mt-5 text-[11px] text-text-3 leading-relaxed">
              Kode ini hidup selama sesi berjalan. Begitu sesi ditutup, kodenya
              mati — sesi berikutnya di room yang sama dapat kode baru.
            </p>
          </>
        )}

        <button onClick={tutup}
          className="mt-6 w-full py-2.5 rounded-xl border border-border text-[13px]">
          Tutup
        </button>
      </div>
    </div>
  )
}


// ── TABLET KAMAR (29 Sep 2026) ──────────────────────────────────────────────
//
// Zaldy: "tab terdaftar yang login di kamar-kamar itu otomatis, nggak usah
// input kode". Kapten memasangkan tablet ke room SEKALI dari sini: tautan
// pasangannya dibuka di tablet itu, sesudahnya remote di tablet terbuka sendiri
// tiap room-nya punya sesi. Tautan = kunci room; jangan dibagikan ke tamu.
type Perangkat = {
  id: string; nama: string; aktif: boolean; created_at: string
  terakhir_at: string | null; room_id: string; room_nama: string; jalur: string
  jenis: 'tablet' | 'tv'; kode_pasang?: string | null
}

function PanelTablet({ room, tutup }: { room: Room; tutup: () => void }) {
  const [daftar, setDaftar] = useState<Perangkat[]>([])
  const [baru, setBaru] = useState<Perangkat | null>(null)
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState(false)
  const [tersalin, setTersalin] = useState(false)

  const muat = useCallback(() => {
    api.get<Perangkat[]>('/api/perangkat', { params: { room_id: room.room_id } })
      .then(r => setDaftar(r.data.filter(p => p.aktif)))
      .catch(e => setGalat(pesanGalat(e)))
  }, [room.room_id])
  useEffect(muat, [muat])

  async function pasang(jenis: 'tablet' | 'tv') {
    setSibuk(true); setGalat('')
    try {
      const r = await api.post<Perangkat & { token: string }>(`/api/room/${room.room_id}/perangkat`, { jenis })
      setBaru({ ...r.data, aktif: true, created_at: new Date().toISOString(), terakhir_at: null, room_id: room.room_id, room_nama: room.room_nama })
      muat()
    } catch (e) { setGalat(pesanGalat(e)) } finally { setSibuk(false) }
  }
  async function cabut(p: Perangkat) {
    if (!confirm(`Cabut "${p.nama}"? Tablet itu akan kembali meminta kode.`)) return
    try { await api.delete(`/api/perangkat/${p.id}`); if (baru?.id === p.id) setBaru(null); muat() }
    catch (e) { setGalat(pesanGalat(e)) }
  }
  function salin(u: string) {
    navigator.clipboard?.writeText(u).then(() => { setTersalin(true); setTimeout(() => setTersalin(false), 1500) })
  }

  const asal = location.origin
  const tautan = baru ? asal + baru.jalur : ''
  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center px-5" onClick={tutup}>
      <div className="bg-bg-1 border border-border rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="text-[12px] text-text-3 uppercase tracking-wide">{room.outlet_nama} · {room.room_nama}</div>
        <h3 className="mt-1 text-[16px] font-semibold">Tablet & TV kamar</h3>
        <p className="mt-1 text-[12px] text-text-3 leading-relaxed">
          Tablet remote dan TV yang dipasangkan ke room ini terbuka sendiri setiap ada sesi — tidak ada kode yang perlu diketik tamu.
        </p>
        {galat && <div className="mt-3 text-[12px] text-red-400">{galat}</div>}

        {baru ? (
          <div className="mt-4 rounded-xl border border-gold/40 bg-gold/[0.06] p-3">
            {baru.kode_pasang && (
              <div className="text-center mb-3">
                <div className="text-[11px] text-text-3 uppercase tracking-wide">Kode pasang — ketik di aplikasi {baru.jenis === 'tv' ? 'TV' : 'tablet'} (15 menit)</div>
                <div className="text-4xl font-bold tracking-[0.25em] text-gold mt-1">{baru.kode_pasang}</div>
              </div>
            )}
            <div className="text-[12px] font-semibold text-gold">Atau buka tautan ini di {baru.jenis === 'tv' ? 'TV' : 'tablet'} kamar (sekali saja):</div>
            <div className="mt-1.5 text-[11.5px] break-all text-text-2 select-all">{tautan}</div>
            <div className="mt-2 flex gap-2">
              <button onClick={() => salin(tautan)} className="text-[12px] px-3 py-1.5 rounded-lg bg-gold text-black font-medium">{tersalin ? 'Tersalin ✓' : 'Salin tautan'}</button>
              <a href={tautan} target="_blank" rel="noreferrer" className="text-[12px] px-3 py-1.5 rounded-lg border border-border">Buka di sini</a>
            </div>
            <p className="mt-2 text-[10.5px] text-text-3 leading-snug">
              Tautan ini adalah kunci room — kirim ke tablet lewat cara internal (bukan ke tamu). Setelah dibuka, tablet menyimpannya dan alamatnya berubah jadi /r.
            </p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button onClick={() => pasang('tablet')} disabled={sibuk}
              className="py-2.5 rounded-xl bg-gold text-black text-[13px] font-semibold disabled:opacity-50">
              {sibuk ? 'Menyiapkan…' : '+ Tablet remote'}
            </button>
            <button onClick={() => pasang('tv')} disabled={sibuk}
              className="py-2.5 rounded-xl border border-gold/50 text-gold text-[13px] font-semibold disabled:opacity-50">
              {sibuk ? 'Menyiapkan…' : '+ TV kamar'}
            </button>
          </div>
        )}

        <div className="mt-4 text-[11px] text-text-3 uppercase tracking-wide">Terpasang</div>
        {daftar.length === 0 ? (
          <div className="mt-1 text-[12px] text-text-3">Belum ada tablet untuk room ini.</div>
        ) : (
          <div className="mt-1 space-y-1.5">
            {daftar.map(p => (
              <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium truncate">{p.nama} <span className="text-[10px] text-text-3 border border-border rounded px-1">{p.jenis === 'tv' ? 'TV' : 'tablet'}</span></div>
                  <div className="text-[10.5px] text-text-3">
                    dipasang {new Date(p.created_at).toLocaleDateString('id-ID')}
                    {p.terakhir_at ? ` · terakhir aktif ${new Date(p.terakhir_at).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' })}` : ' · belum pernah dibuka'}
                  </div>
                </div>
                <button onClick={() => cabut(p)} className="text-[11px] text-red-400 border border-red-500/30 rounded-lg px-2 py-1">Cabut</button>
              </div>
            ))}
          </div>
        )}

        <button onClick={tutup} className="mt-5 w-full py-2.5 rounded-xl border border-border text-[13px]">Tutup</button>
      </div>
    </div>
  )
}
